import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "next-intl";
import { PageHeader } from "@/components/page-header";
import { CaseSummary } from "@/components/cases/case-summary";
import { CaseSidePanel } from "@/components/cases/case-side-panel";
import { CaseUpcomingEvents } from "@/components/cases/case-upcoming-events";
import { CaseStepsMain } from "@/components/cases/case-steps-main";
import { EventDialog } from "@/components/calendar/event-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useCaseDetail } from "@/hooks/use-case-detail";
import { useEventDialog } from "@/hooks/use-event-dialog";
import { useAuth } from "@/components/auth-provider";
import { useAccountDetail } from "@/hooks/use-account-detail";

function CaseDetailSkeleton() {
  return (
    <div className="grid min-h-0 flex-1 gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:grid-rows-[1fr] lg:overflow-hidden xl:grid-cols-[minmax(0,1fr)_380px]">
      <div className="flex min-h-0 min-w-0 flex-col gap-4 lg:overflow-y-auto">
        <Card className="@container/summary shrink-0">
          <CardHeader>
            <Skeleton className="h-5 w-24" />
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 @md/summary:grid-cols-2 @2xl/summary:grid-cols-3 @4xl/summary:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="min-w-0 space-y-1">
                <Skeleton className="h-3 w-20 max-w-full" />
                <Skeleton className="h-4 w-32 max-w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      </div>
      <div className="min-w-0 lg:sticky lg:top-0 lg:self-start">
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-24" />
          </CardHeader>
          <CardContent className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_dashboard/cases/$id")({
  validateSearch: (search: Record<string, unknown>) => {
    const uuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return {
      step: typeof search.step === "string" && uuidPattern.test(search.step)
        ? search.step
        : undefined,
      message: typeof search.message === "string" && uuidPattern.test(search.message)
        ? search.message
        : undefined,
    };
  },
  component: CaseDetailPage,
});

function CaseDetailPage() {
  const { id } = Route.useParams();
  const { step: focusStepId, message: focusMessageId } = Route.useSearch();
  const t = useTranslations("CaseDetail");
  const { user } = useAuth();
  const { data: account } = useAccountDetail();
  const {
    data: detail,
    focusedMessage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useCaseDetail(id, focusMessageId);
  const { openEvent, openNew, dialogProps } = useEventDialog();

  if (isLoading) {
    return (
      <>
        <PageHeader
          title="..."
          parent={{ label: t("breadcrumbParent"), href: "/cases" }}
        />
        <CaseDetailSkeleton />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <PageHeader title="—" parent={{ label: t("breadcrumbParent"), href: "/cases" }} />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center">
          <p className="text-muted-foreground">{t("loadError")}</p>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? t("retrying") : t("retry")}
          </Button>
        </div>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <PageHeader title="—" parent={{ label: t("breadcrumbParent"), href: "/cases" }} />
        <div className="flex flex-1 items-center justify-center p-4">
          <p className="text-muted-foreground">{t("notFound")}</p>
        </div>
      </>
    );
  }

  const {
    caseData,
    steps,
    journal,
    userNames,
    clients,
    assignees,
    clientNames,
  } = detail;

  // Ensure the current user's name is always available in the lookup
  const allUserNames = { ...userNames };
  if (user && !allUserNames[user.id]) {
    allUserNames[user.id] = user.name;
  }
  const canEditCase = account?.roles.includes("admin") ?? false;

  return (
    <>
      <PageHeader
        title={caseData.title}
        parent={{ label: t("breadcrumbParent"), href: "/cases" }}
      />
      <div className="grid min-h-0 flex-1 gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:grid-rows-[1fr] lg:overflow-hidden xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-h-0 min-w-0 flex-col gap-4 lg:overflow-y-auto">
          <CaseSummary
            caseData={caseData}
            clients={clients}
            assignees={assignees}
            clientNames={clientNames}
            canEdit={canEditCase}
            onUpdated={refetch}
          />
          <CaseStepsMain
            caseId={id}
            caseTypeId={caseData.type?.id}
            initialSteps={steps}
            focusStepId={focusStepId}
          />
          {focusMessageId && (
            <Card id={`case-message-${focusMessageId}`}>
              <CardHeader>{t("messages")}</CardHeader>
              <CardContent>
                {focusedMessage?.body ?? t("messageUnavailable")}
              </CardContent>
            </Card>
          )}
        </div>
        <div className="flex min-h-0 min-w-0 flex-col gap-4">
          <CaseUpcomingEvents
            caseId={id}
            onEventClick={openEvent}
            onNewEvent={() => openNew({ case_id: id })}
          />
          <CaseSidePanel
            caseId={id}
            initialEntries={journal}
            currentUserId={user?.id ?? ""}
            userNames={allUserNames}
          />
        </div>
      </div>
      <EventDialog {...dialogProps} />
    </>
  );
}
